import { useCallback } from "react"
import { useMutation } from "urql"

import type { ResendToIndividualPersonInput, ResendToIndividualPersonMutation } from "@/graphql/codegen/graphql"
import { ResendToIndividualPersonDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseReSendToIndividualPersonReturn {
  resendToIndividualPerson: (input: ResendToIndividualPersonInput) => Promise<boolean>
  isPromoting: boolean
}

export const useReSendToIndividualPerson = (): UseReSendToIndividualPersonReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    ResendToIndividualPersonMutation,
    { input: ResendToIndividualPersonInput }
  >(ResendToIndividualPersonDocument)

  const resendToIndividualPerson = useCallback(
    async (input: ResendToIndividualPersonInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.resendToIndividualPerson
      const hasError = error || output?.__typename === "ResendToIndividualPersonFailure"

      if (hasError) {
        notify({ type: NotificationType.Error, message: "Unable to complete action. Please try again." })
        return false
      }

      notify({ type: NotificationType.Success, message: "Price changes resent successfully." })
      return true
    },
    [executeMutation, notify]
  )

  return { resendToIndividualPerson, isPromoting: fetching }
}
